import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Message, ChatRequest, ChatRequestState, ChatRequestContext } from './types';
import { agentStreamManager } from './agentStreamManager';
import { useOutlookStore, useAppointmentStore, useSettingStore, useAttendeesStore, useAgentChatStore } from '@shared/stores';
import { useAuthStore } from '@/pages/taskpane/features/auth/store/authStore';

declare const Office: any;

// Logger utility - safe check for development mode
const isDevelopment = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';

const logger = {
    debug: (...args: any[]) => {
        if (isDevelopment) {
            console.debug(...args);
        }
    },
    warn: (...args: any[]) => {
        if (isDevelopment) {
            console.warn(...args);
        }
    },
    error: (...args: any[]) => {
        console.error(...args);
    }
};

export const useAgentChat = () => {
    // Subscribe to store state directly (no local state needed)
    const messages = useAgentChatStore((state) => state.messages);
    const isLoading = useAgentChatStore((state) => state.isLoading);
    const thinkingStep = useAgentChatStore((state) => state.thinkingStep);
    const threadId = useAgentChatStore((state) => state.threadId);
    
    // Get store actions
    const agentChatStore = useAgentChatStore();

    // Get auth store for token access
    const authStore = useAuthStore();
    const { i18n } = useTranslation();

    // Subscribe to stores to get latest values and ensure callback is recreated when values change
    const { organizer, timeZone: outlookTimeZone } = useOutlookStore();
    const { timeZone: appointmentTimeZone, startDateTimeUtc, endDateTimeUtc, endDateTimeZone, startDateTimeZone } = useAppointmentStore();
    const { defaultResourceType, defaultLocations, defaultCategoryIds } = useSettingStore();
    const { requiredAttendees, optionalAttendees } = useAttendeesStore();

    // Calculate timeZone (appointment store takes priority) - memoized
    const timeZone = useMemo(() => appointmentTimeZone || outlookTimeZone || null, [appointmentTimeZone, outlookTimeZone]);

    const generateThreadId = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // Initialize threadId if empty
    useEffect(() => {
        if (!threadId) {
            agentChatStore.setThreadId(generateThreadId());
        }
    }, [threadId, agentChatStore]);

    const resetChat = useCallback(() => {
        // Cancel any active stream
        agentStreamManager.cancelStream();
        
        const newThreadId = generateThreadId();
        
        // Clear store (this also aborts stream via store's clearChat)
        agentChatStore.clearChat();
        agentChatStore.setThreadId(newThreadId);
    }, [agentChatStore]);

    const sendMessage = useCallback(async (content: string) => {
        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content,
            timestamp: Date.now(),
        };

        // Add user message to store
        agentChatStore.addMessage(userMessage);
        agentChatStore.setIsLoading(true);

        // Read subject directly from Outlook API to get the latest value
        // This ensures we always have the current subject even if user changes it while add-in is open
        let latestSubject = ""; // Fallback to store value
        try {
            if (Office && Office.context && Office.context.mailbox && Office.context.mailbox.item) {
                const item = Office.context.mailbox.item;
                if (item.itemType === Office.MailboxEnums.ItemType.Appointment && item.subject) {
                    await new Promise<void>((resolve) => {
                        item.subject.getAsync((result: Office.AsyncResult<string>) => {
                            if (result.status === Office.AsyncResultStatus.Succeeded) {
                                latestSubject = result.value || "";
                                logger.debug('[Agent Chat] Subject read from Outlook API:', latestSubject);
                            } else {
                                logger.warn('[Agent Chat] Failed to read subject from Outlook API');
                            }
                            resolve();
                        });
                    });
                }
            }
        } catch (error) {
            logger.warn('[Agent Chat] Error reading subject from Outlook API:', error);
        }

        const assistantMessageId = (Date.now() + 1).toString();
        const assistantMessage: Message = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            timestamp: Date.now(),
        };

        // Add assistant message to store
        agentChatStore.addMessage(assistantMessage);

        // Get access token from authStore (with scopes needed for Outlook operations)
        let accessToken: string | undefined;
        try {
            // Request scopes needed for Outlook calendar operations
            const scopes = ['user.read', 'Calendars.Read', 'Calendars.ReadWrite'];
            accessToken = await authStore.getValidToken(scopes);
            if (!accessToken) {
                logger.warn('[Token Flow] FE: No token retrieved from authStore');
            }
        } catch (error) {
            logger.warn('[Token Flow] FE: Failed to get access token for agent chat:', error);
            // Continue without token - some operations may not work, but don't block the request
        }

        // Build state object (preferred format) - following web-chat pattern
        const state: ChatRequestState = {
            source: "outlook-addin",
            language: i18n.language || 'en'
        };

        if (organizer) {
            state.user_email = organizer;
            state.user_id = organizer;
        }

        if (startDateTimeUtc) {
            state.meeting_start_time = startDateTimeUtc;
        }

        if (endDateTimeUtc) {
            state.meeting_end_time = endDateTimeUtc;
        }

        // Auto-generate occurrences from meeting times for availability checking
        if (startDateTimeUtc && endDateTimeUtc && startDateTimeZone && endDateTimeZone) {
            state.occurrences = [{
                utcStartDate: startDateTimeUtc,
                utcEndDate: endDateTimeUtc,
                startDate: startDateTimeZone,
                endDate: endDateTimeZone
            }];
        }

        if (timeZone) {
            state.time_zone = timeZone;
        }

        // Build user preferences if has values
        const userPreferences: any = {};
        if (defaultResourceType) {
            userPreferences.default_resource_type = defaultResourceType;
        }
        if (defaultLocations) {
            userPreferences.default_locations = {};
            // Tìm location_ids tương ứng với type đang chọn
            const typeSpecificLocationIds = defaultLocations.defaultResourceTypes?.find(
                (rt: any) => rt.type.toLowerCase() === defaultResourceType.toLowerCase()
            )?.locationIds;

            // Ưu tiên locationIds theo type, nếu không có thì dùng defaultLocationIds chung
            userPreferences.default_locations.default_location_ids = typeSpecificLocationIds || [''];
        }

        if (defaultCategoryIds) {
            userPreferences.default_categories = defaultCategoryIds;
        }
        if (Object.keys(userPreferences).length > 0) {
            state.user_preferences = userPreferences;
        }

        // Build meeting context if has values
        const allAttendees = [
            ...requiredAttendees?.map(attendee => attendee.email) || [],
            ...optionalAttendees?.map(attendee => attendee.email) || []
        ];
        const meetingContext: any = {};
        if (latestSubject && latestSubject.trim()) {
            meetingContext.subject = latestSubject;
        }
        if (allAttendees.length > 0) {
            meetingContext.attendees = allAttendees;
        }
        if (Object.keys(meetingContext).length > 0) {
            state.meeting_context = meetingContext;
        }

        // Include token in state (fallback if Authorization header is not used)
        if (accessToken) {
            state.access_token = accessToken;
        }

        // Build legacy context object (for backward compatibility)
        // Only include context if state is not provided (following web-chat pattern)
        const context: ChatRequestContext = {
            source: "outlook-addin"
        };

        if (organizer) {
            context.user_email = organizer;
        }
        if (latestSubject && latestSubject.trim()) {
            context.subject = latestSubject;
        }
        if (allAttendees.length > 0) {
            context.attendees = allAttendees;
        }
        if (startDateTimeUtc) {
            context.meeting_start_time = startDateTimeUtc;
        }
        if (endDateTimeUtc) {
            context.meeting_end_time = endDateTimeUtc;
        }
        // Auto-generate occurrences from meeting times for availability checking
        if (startDateTimeUtc && endDateTimeUtc) {
            context.occurrences = [{
                utcStartDate: startDateTimeUtc,
                utcEndDate: endDateTimeUtc,
                startDate: startDateTimeUtc,
                endDate: endDateTimeUtc
            }];
        }
        if (timeZone) {
            context.time_zone = timeZone;
        }
        if (defaultResourceType) {
            context.default_resource_type = defaultResourceType;
        }
        if (defaultLocations) {
            context.default_locations = {};
            const typeSpecificLocationIds = defaultLocations.defaultResourceTypes?.find(
                (rt: any) => rt.type === defaultResourceType
            )?.locationIds;

            context.default_locations.default_location_ids = typeSpecificLocationIds || defaultLocations.defaultLocationIds;
        }

        // if (defaultCategoryIds) {
        //     context.default_category_ids = defaultCategoryIds;
        // }

        if (accessToken) {
            context.access_token = accessToken;
            context.token = accessToken;
        }

        // Build payload - following web-chat pattern
        // state is always included (preferred method)
        // context is only included if state is not provided (for backward compatibility)
        const payload: ChatRequest = {
            message: content,
            thread_id: threadId,
            state: state,
            // Only include context if state is not provided and context exists (backward compatibility)
            context: state ? undefined : (Object.keys(context).length > 1 ? context : undefined)
        };

        // Use agentStreamManager to handle streaming
        // This ensures stream continues even if component unmounts during navigation
        try {
            await agentStreamManager.startStream(
                payload,
                assistantMessageId,
                accessToken
            );
        } catch (error) {
            logger.error("❌ useAgentChat: Stream error", error);
            // Error already handled by streamManager
        }
    }, [threadId, authStore, organizer, timeZone, startDateTimeUtc, endDateTimeUtc, startDateTimeZone, endDateTimeZone, defaultResourceType, defaultLocations, defaultCategoryIds, requiredAttendees, optionalAttendees, agentChatStore]);

    return {
        messages,
        isLoading,
        thinkingStep,
        sendMessage,
        resetChat,
    };
};
